import { useContext, useMemo } from 'react';
import { Box, Spinner, Text } from '@usealloy/component-library';

import Collapsibox from '~/components/Collapsibox';
import { FraudSignalScoreBadge } from '~/components/FraudSignalScoreBadge';
import { FraudSignalIndicators } from '~/entities/components/FraudSignalIndicators';
import { useEntityFraudSnapshotByEvaluation } from '~/entities/hooks';
import { UserContext } from '~/dashboard/context';

const FraudSignalHeader = ({ score }: { score: number | null }) => (
  <Box direction="row" justifyContent="space-between">
    <Text size={4} weight={500}>Fraud Signal</Text>
    <FraudSignalScoreBadge fraudSignalScore={score} />
  </Box>
);

interface FraudSignalCardProps {
  evaluationToken: string;
  hasFraudSignalService: boolean;
}

export const FraudSignalCard = ({
  evaluationToken,
  hasFraudSignalService,
}: FraudSignalCardProps) => {
  const { featureFlags } = useContext(UserContext);
  const showScoreExplainabilityChanges = Boolean(featureFlags?.fraudSignalScoreExplainability);

  const {
    data: snapshotByEvaluationData,
    isFetching: isSnapshotByEvaluationLoading,
  } = useEntityFraudSnapshotByEvaluation({
    evaluationToken,
    enabled: showScoreExplainabilityChanges && hasFraudSignalService,
  });

  const snapshotForAlert = useMemo(() => {
    const byEval = snapshotByEvaluationData;
    if (byEval?.score != null && String(byEval.score).trim() !== '') {
      return {
        score: byEval.score,
        indicators: byEval.indicators ?? [],
      };
    }
    return undefined;
  }, [snapshotByEvaluationData]);

  const evaluationFraudSignalScore = snapshotForAlert != null
    ? parseFloat(String(snapshotForAlert.score))
    : NaN;

  const maxIndicators = useMemo(() => {
    if (snapshotForAlert == null || Number.isNaN(evaluationFraudSignalScore)) return [];
    return snapshotForAlert.indicators.slice(0, 4);
  }, [snapshotForAlert, evaluationFraudSignalScore]);

  const hasFraudSignalSnapshot = Boolean(snapshotForAlert)
    && !Number.isNaN(evaluationFraudSignalScore);

  if (!showScoreExplainabilityChanges || !hasFraudSignalService) return null;
  if (!isSnapshotByEvaluationLoading && !hasFraudSignalSnapshot) return null;

  return (
    <Collapsibox
      header={(
        <FraudSignalHeader score={evaluationFraudSignalScore} />
      )}
    >
      {isSnapshotByEvaluationLoading ? (
        <Box justifyContent="center" style={{ padding: 'var(--cl-space-4) 0' }}>
          <Spinner />
        </Box>
      ) : (
        <FraudSignalIndicators
          indicators={maxIndicators}
          score={evaluationFraudSignalScore}
          twoColumns
        />
      )}
    </Collapsibox>
  );
};
